import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormSpy } from "react-final-form";

import { FormContext } from "../../context/form-context";
import {
  measurementRange,
  composeValidators,
  maxLength,
  required,
  valueIsNumber,
} from "../../helpers/formValidators";

import { addDateAndTimeFields } from "./Duration";

import {
  FormLabel,
  FormInputHolder,
  FormInput,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
  FormTimeInput,
  FormDateInput,
} from "../form";

import ErrorMessage from "../ErrorMessage/ErrorMessage";
import { PrimaryButton, SecondaryButton } from "../buttons";
import babyIcons from "../icons/babyIcons";
import { Error } from "../form/BaseForm";
import { OnChange } from "react-final-form-listeners";

const GrowthForm = (props) => {
  const {
    selectedProfile,
    babies,
    profileOptions,
    growthRecordsPatch,
    growthRecordsPost,
    accountId,
    handleRecords,
    showUnSavedModal,
    hideTrackerModals,
    showFirstTimeSavingModal,
    showSaveEntryModal,
  } = props;

  const defaultValues = {
    babyId: null,
    providerId: selectedProfile.id,
    weight: "100.00",
    bodyLength: "10.00",
    headCircumference: "5.0",
    date: new Date(),
    time: new Date(),
  };

  const [initialValues, setInitialValues] = useState(undefined);

  useEffect(() => {
    // if there's form data in the context, use that instead of using default values
    if (formContext && Object.keys(formContext.formValues).length > 0) {
      setInitialValues(formContext.formValues);
      return
    }

    const latestRecord = handleRecords('growth', null) || {};

    setInitialValues({
      ...defaultValues,
      ...latestRecord
    });
  }, []);

  const formContext = useContext(FormContext);

  const toggleWarningModal = () => {
    return formContext.isDirty ? showUnSavedModal() : hideTrackerModals();
  };

  const onSubmit = (values) => {
    const recordBody = {
      ...values,
      providerId: values.providerId === "other" ? null : values.providerId,
      otherProvider: values.providerId !== "other" ? null : values.otherProvider,
      date: addDateAndTimeFields(values.date, values.time),
    };

    if (values.id) {
      // Update existing record
      return growthRecordsPatch(
        { accountId: accountId, babyId: values.babyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => {
          if (data) {
            hideTrackerModals();
          }
        })
        .catch((error) => <ErrorMessage />);
    } else {
      // Create new record
      return growthRecordsPost(
        {
          accountId: accountId,
          babyId: babies.length === 1 ? babies[0].id : values.babyId,
        },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => {
          if (data) {
            hideTrackerModals();
          }
        })
        .catch((error) => <ErrorMessage />);
    }
  };

  const onSave = (values) => {
    if (!showFirstTimeSavingModal) {
      onSubmit(values);
    } else {
      formContext.setValues(values);
      formContext.setFunctions({
        onSubmit: onSubmit,
      });

      return showSaveEntryModal();
    }
  };

  if (!initialValues) {
    return <div></div>
  }

  return (
    <BaseForm
      onSubmit={onSave}
      initialValues={initialValues}
    >
      <h2>Track Growth</h2>

      {babies && babies.length > 1 && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map((baby, index) => (
              <FormRadio
                key={baby.id}
                value={baby.id}
                name="babyId"
                imageSrc={babyIcons[baby.icon]}
                validate={required}
              >
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>

          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) {
                return;
              }

              const latestRecord =  handleRecords('growth', value) || {};

              setInitialValues({
                ...defaultValues,
                ...latestRecord,
                babyId: value,
                weight: parseFloat(latestRecord.weight).toFixed(2),
                bodyLength: parseFloat(latestRecord.bodyLength).toFixed(2),
                headCircumference: parseFloat(latestRecord.headCircumference).toFixed(1),
              });
            }}
          </OnChange>

          <Error name="babyId" />
        </React.Fragment>
      )}


      <FormLabel for="providerId">Provider</FormLabel>
      <FormSelect
        name="providerId"
        options={profileOptions}
        validate={required}
        includeOther
        otherName="otherProvider"
      />

      <FormInputHolder>
        <div>
          <FormInput
            name="weight"
            maxLength={7}
            validate={composeValidators(
              measurementRange([100.0, 8000.0]),
              maxLength(8),
              valueIsNumber
            )}
            labelRenderer={() => (
              <FormLabel for="weight" note="Optional">
                Baby Weight
              </FormLabel>
            )}
            type="number"
            measurement="g"
            format={(value) => (value ? parseFloat(value).toFixed(2) : "0.00")}
            formatOnBlur
          />
        </div>
      </FormInputHolder>

      <FormInputHolder>
        <div>
          <FormInput
            name="bodyLength"
            maxLength={6}
            validate={composeValidators(
              measurementRange([10, 100]),
              maxLength(7),
              valueIsNumber
            )}
            labelRenderer={() => (
              <FormLabel for="bodyLength" note="Optional">
                Baby Length
              </FormLabel>
            )}
            type="number"
            measurement={"cm"}
            format={(value) => (value ? parseFloat(value).toFixed(2) : "0.00")}
            formatOnBlur
          />
        </div>
      </FormInputHolder>

      <FormInputHolder>
        <div>
          <FormInput
            name="headCircumference"
            maxLength={6}
            validate={composeValidators(
              measurementRange([5, 100]),
              maxLength(7),
              valueIsNumber
            )}
            labelRenderer={() => (
              <FormLabel for="headCircumference" note="Optional">
                Head Circumference
              </FormLabel>
            )}
            measurement={"cm"}
            type="number"
            format={(value) => (value ? parseFloat(value).toFixed(2) : "0.00")}
            formatOnBlur
          />
        </div>
      </FormInputHolder>

      <FormInputHolder>
        <FormDateInput
          name="date"
          validate={required}
          labelRenderer={() => <FormLabel for="date">Date</FormLabel>}
          dateFormat="MMM dd, Y"
        />

        <FormTimeInput
          name="time"
          validate={required}
          labelRenderer={() => <FormLabel for="time">Time</FormLabel>}
        />
      </FormInputHolder>

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => {
          return (
            <FormInputHolder>
              <SecondaryButton
                type="button"
                label="Cancel"
                onClick={toggleWarningModal}
              />

              <PrimaryButton type="submit" label="Save" disabled={submitting} />
            </FormInputHolder>
          );
        }}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, dirtyFields: true, visited: true, values: true }}
        onChange={({ dirty, dirtyFields, visited, values }) => {
          if (dirty) {
            let actuallyDirty = false;

            formContext.setValues(values);

            Object.keys(visited).forEach((key) => {
              if (visited[key]) {
                actuallyDirty = true;
              }
            });

            actuallyDirty ? formContext.setDirty() : formContext.notDirty();
          }
        }}
      />
    </BaseForm>
  )
};

GrowthForm.propTypes = {
  babies: PropTypes.arrayOf(Object),
  accountId: PropTypes.string,
  profiles: PropTypes.arrayOf(Object),
  growthRecordsPost: PropTypes.func,
  growthRecordsPatch: PropTypes.func,
  showUnSavedModal: PropTypes.func,
  handleRecords: PropTypes.func,
  hideTrackerModals: PropTypes.func,
  profileOptions: PropTypes.arrayOf(Object),
  selectedProfile: PropTypes.object,
  showFirstTimeSavingModal: PropTypes.bool,
  growthRecordsLatest: PropTypes.func,
  showSaveEntryModal: PropTypes.func,
};

export default GrowthForm;


// WEBPACK FOOTER //
// ./src/components/ActivityTracker/GrowthForm.js